import style from '../style.scss'
import { lang } from 'enablex/shared'
import React, { FC, useState, useEffect } from 'react'
import TableContainer from './TableContainer'
import BackIcon from '~/icons/BackIcon'
import api from '~/app/api'
import Select from 'react-select'
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Rectangle
} from 'recharts'

import { AppState } from '~/app/rootReducer'
import { connect, ConnectedProps } from 'react-redux'
import { fetchAgentsStatsData, toggleAnalyticsWindow } from '../reducer'
import PerformanceGraph from './PerformanceGraph'
import PerformancePieChart from './PerformancePieChart'

type Props = {
    isOpen: boolean
} & ConnectedProps<typeof connector>

function getRandomColor() {
    const letters = '0123456789ABCDEF'
    let color = '#'
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
    }
    return color
}

const ReportingModal: FC<Props> = props => {
    const [selectedAgentStatsData, setSelectedAgentStatsData]: any = useState([])

    const [AgentList, setAgentList]: any = useState([{ label: 'All Agentss', value: 'all' }])
    const [livePerformanceMatrics, setLivePerformanceMatrics] = useState({ linkedAgents: 0, unlinkedAgents: 0, linkedBots: 0, unlinkedBots: 0, avgLiveSessions: 0 })
    const [agentSelected, setAgentSelected] = useState(false)
    const [selectedOption, setSelectedOption] = useState(AgentList[0])
    const [selectedAgentID, setSelectedAgentID] = useState('')
    const [botNames, setBotNames] = useState({})
    const [agentBotAssignments, setAgentBotAssignments] = useState([])

    function handleSelectedAgentID(option) {
        if (option.value === 'all') {
            setSelectedAgentID('')
            setAgentSelected(false)
            setSelectedAgentStatsData([])
            setAgentBotAssignments([])
        } else {
            const { id, email } = option.value
            setSelectedAgentID(id)
            setAgentSelected(true)
        }
    }

    const handleOptionSelect = (option) => {
        handleSelectedAgentID(option)
        setSelectedOption(option)
    }

    function formatDate(dateString, dateOnly) {
        if (!dateString) {
            return ''
        }
        let date = new Date(dateString)
        let day = String(date.getUTCDate()).padStart(2, '0')
        let month = String(date.getUTCMonth() + 1).padStart(2, '0') // Months are zero-based
        let year = date.getUTCFullYear()
        let formattedDate
        if (dateOnly) {
            // Format the date as dd/mm/yyyy
            formattedDate = `${day}/${month}/${year}`
        } else {
            // Get the hours and minutes components
            let hours = date.getUTCHours()
            let minutes = String(date.getUTCMinutes()).padStart(2, '0')

            // Determine AM/PM and convert hours from 24-hour to 12-hour format
            let period = hours >= 12 ? 'PM' : 'AM'
            hours = hours % 12
            hours = hours ? hours : 12 // the hour '0' should be '12'
            let hour = String(hours).padStart(2, '0')

            // Format the date and time as dd/mm/yyyy hh:mm am/pm
            formattedDate = `${day}/${month}/${year} ${hour}:${minutes} ${period}`
        }
        return formattedDate
    }

    useEffect(() => {
        let agentList: any = [{ label: 'All Agents', value: 'all' }]

        // Live Performance Matrics
        // Get current user's role from localStorage
        const currentUserRole = localStorage.getItem('enx/channel-web/user-role')
        const currentUserEmail = props.profile?.email?.toLowerCase()

        // Calculate total agents - include supervisors only if current user is supervisor
        let totalAgents = props.users?.filter(users => users.role === 'agent').length
        if (currentUserRole === 'supervisor') {
            // For supervisors: count themselves + agents assigned to them
            const assignedAgents = props.users?.filter(users =>
                users.role === 'agent' && users.supervisor_id === currentUserEmail
            ).length || 0
            totalAgents = assignedAgents + 1 // +1 for the supervisor themselves
        }

        const allAgents = new Set(props.agentsStatsData?.filter(agent => (agent.botId)).map(agent => `${agent.agentId}_${agent.email}`))
       // const allAgents = new Set(props.agentsStatsData?.map(agent => `${agent.agentId}_${agent.email}`))
        const linkedAgents = new Set(props.agentsStatsData?.filter(agent => (agent.botId && agent.linkedAgent)).map(agent => `${agent.agentId}_${agent.email}`))
        const linkedAgenstSize = linkedAgents.size
        const linkedBots = new Set(props.agentsStatsData?.filter(agentData => (agentData.botId !== '' && agentData.linkedAgent))).size
        const totalBots = props.bots?.length ? props.bots.length : 0
        const liveSessions = props.agentsStatsData?.reduce((accumulator, item) => {
            return accumulator += item.totalRequests - item.resolvedRequests - item.expiredRequests - item.unassignedRequests
        }, 0)
        const avgLiveSessions = liveSessions && liveSessions > 0 ? liveSessions / linkedAgenstSize : 0

        let livePerformanceMatrics = {
            linkedAgents: linkedAgents.size | 0,
            unlinkedAgents: totalAgents ? totalAgents - linkedAgents.size : 0,
            linkedBots: linkedBots | 0,
            unlinkedBots: totalBots - linkedBots,
            avgLiveSessions
        }

        // console.log('livePerformanceMatrics: ', livePerformanceMatrics)

        setLivePerformanceMatrics((prevState) => {
            return {
                ...prevState,
                ...livePerformanceMatrics
            }
        })

        Array.from(allAgents).forEach((data) => {
          const parts = data.split('_')
          const agentId = parts[0]
          const agentEmail = parts.slice(1).join('_') // Handle multiple parts of email
          const formattedLabel = agentEmail.charAt(0).toUpperCase() + agentEmail.slice(1)
          agentList.push({ label: formattedLabel, value: { id: agentId, email: agentEmail } })
        })

        // If current user is supervisor, add them to the agent list
        if (currentUserRole === 'supervisor' && currentUserEmail) {
            // Check if supervisor is not already in the agent list
            const supervisorAlreadyInList = agentList.some(agent =>
                agent.value !== 'all' && agent.value.email.toLowerCase() === currentUserEmail
            )

            if (!supervisorAlreadyInList) {
                const formattedLabel = currentUserEmail.charAt(0).toUpperCase() + currentUserEmail.slice(1)
                agentList.push({
                    label: formattedLabel,
                    value: { id: currentUserEmail, email: currentUserEmail }
                })
            }
        }

        setAgentList(agentList)

        const botMap = props.bots.reduce((map, bot) => {
            map[bot.id] = bot.name
            return map
        }, {})

        setBotNames(botMap)

        // console.log('botNames:: ', botMap)
        // console.log('agentStatsData: ', props.agentsStatsData)
        // console.log('Bots : ', props.bots)
    }, [props.profile])

    useEffect(() => {
        async function fetchData() {
            try {
                const { data: currentAgentsStatsData } = await api.getSecured().get(`/admin/workspace/collaborators/currentAgentStatsData/${selectedAgentID}`)
                const currentData = currentAgentsStatsData.payload
                const agentsData = currentData?.map((data) => {
                    let filteredData = {}
                    const totalRequests = data.resolvedRequests
                    currentAgentHeadingKeys.forEach(key => {
                        if (data.hasOwnProperty(key)) {
                            if (key === 'createdAt' || key === 'firstRequestTime') {
                                filteredData[key] = formatDate(data[key], true)
                            } else if (key === 'lastRequestTime') {
                                filteredData[key] = formatDate(data[key], false)
                            } else if (key === 'resolveTime') {
                                if (totalRequests > 0) {
                                    const timeInSecs = data[key] / totalRequests
                                    filteredData[key] = timeInSecs
                                } else {
                                    filteredData[key] = ''
                                }
                            } else {
                                filteredData[key] = data[key]
                            }
                        }
                    })
                    return filteredData
                }
                )
                setSelectedAgentStatsData(agentsData)

                // Fetch agent's bot assignments
                try {
                    const { data: agentBotsData } = await api.getSecured().get(`/admin/workspace/bots/${selectedAgentID}/getBotsForAgent`)
                    const botIds = agentBotsData.payload || []

                    // Create bot assignment records for the TableContainer
                    const botAssignments = botIds.map(botId => ({
                        botId,
                        totalRequests: 0,
                        resolvedRequests: 0,
                        unassignedRequests: 0,
                        expiredRequests: 0,
                        videoSessions: 0,
                        voiceSessions: 0,
                        resolveTime: 0,
                        linkedAgent: true
                    }))

                    setAgentBotAssignments(botAssignments)
                } catch (botError) {
                    console.error('Error fetching agent bot assignments:', botError)
                    setAgentBotAssignments([])
                }

                // console.log('agentsData:: ', agentsData)
            } catch (error) {
                setSelectedAgentStatsData([])
                setAgentBotAssignments([])
            }
        }
        if (agentSelected) {
            void fetchData()
        }
    }, [selectedAgentID])

    // Performance matrics list
    const performanceMatricsList = [
        { label: 'Total Requests', value: 'totalRequests' },
        { label: 'Resolved Requests', value: 'resolvedRequests' },
        { label: 'Unassigned Requests', value: 'unassignedRequests' },
        { label: 'Expired Requests', value: 'expiredRequests' },
        { label: 'Resolve time', value: 'resolveTime' },
        { label: 'Video Sessions', value: 'videoSessions' },
        { label: 'Audio Only Sessions', value: 'audioOnlySessions' },
        { label: 'Voice Sessions', value: 'voiceSessions' }
    ]

    // Performance Pir chart list
    const performancePieChartList = {
        'resolvedRequests': 'Resolved Requests',
        'resolveTime': 'Resolve Time'
    }

    //Bot Name in heading have id as botId
    const currentAgentHeading = [
        'Bot Name',
        'Created',
        'Started',
        'Last Interaction',
        'Total Requests',
        'Resolved Requests',
        'Unassigned Requests',
        'Expired Requests',
        'Average Resolve Time',
        'Video Sessions',
        'Voice Sessions',
        'App to Phone Sessions'
    ]
    const currentAgentHeadingKeys = [
        'botId',
        'createdAt',
        'firstRequestTime',
        'lastRequestTime',
        'totalRequests',
        'resolvedRequests',
        'unassignedRequests',
        'expiredRequests',
        'resolveTime',
        'videoSessions',
        'audioOnlySessions',
        'voiceSessions',
        'linkedAgent'
    ]
    const workspaceHeading = [
        'Agent ID',
        'Created',
        'Bots Linked',
        'Total Requests',
        'Resolved Requests',
        'Unassigned Requests',
        'Expired Requests',
        'Average Resolve Time',
        'Video Sessions',
        'Voice Sessions',
        'App to Phone Sessions'
    ]
    const workspaceHeadingKeys = [
        'email',
        'createdAt',
        'botsLinked',
        'totalRequests',
        'resolvedRequests',
        'unassignedRequests',
        'expiredRequests',
        'resolveTime',
        'videoSessions',
        'audioOnlySessions',
        'voiceSessions'
    ]

    const Bar1data = [
        {
            name: 'Agents',
            Value1: livePerformanceMatrics.linkedAgents,
            Value2: livePerformanceMatrics.unlinkedAgents,
            Value1Color: '#8884d8',
            Value2Color: '#82ca9d'
        },
        {
            name: 'Bots',
            Value1: livePerformanceMatrics.linkedBots,
            Value2: livePerformanceMatrics.unlinkedBots,
            Value1Color: '#8884d8',
            Value2Color: '#82ca9d'
        },
        {
            name: 'Sessions',
            Value1: livePerformanceMatrics.avgLiveSessions,
            Value1Color: '#8884d8'
        },
    ]

    const Bar2data = [
        {
            name: 'Available',
            value1: 24,
            Value1Color: '#43A047'
        },
        {
            name: 'Engaged',
            value1: 20,
            Value1Color: '#FB8C00'
        },
        {
            name: 'Offline',
            value1: 2,
            Value1Color: '#a8a6a3'
        },
    ]

    const AgentPerformanceColors = {
        totalRequests: '#FFD700',    // Light Gold
        resolvedRequests: '#90EE90', // Light Green
        unassignedRequests: '#ADD8E6', // Light Blue
        expiredRequests: '#FFB6C1', // Light Pink
        resolveTime: '#FFA07A',     // Light Salmon
        videoSessions: '#E6E6FA',   // Light Lavender
        voiceSessions: '#FFFACD'    // Light Lemon Chiffon
    }

    const performanceDefaultColor = '#8884d8'
    const performanceBackgroundColor = '#eee'

    const BotPerformanceColors = {
        totalRequests: '#FFD700',    // Light Gold
        resolvedRequests: '#90EE90', // Light Green
        unassignedRequests: '#ADD8E6', // Light Blue
        expiredRequests: '#FFB6C1', // Light Pink
        resolveTime: '#FFA07A',     // Light Salmon
        videoSessions: '#E6E6FA',   // Light Lavender
        voiceSessions: '#FFFACD'    // Light Lemon Chiffon
    }

    let renderLabel = function (entry) {
        // console.log(entry)
        // return entry.name;
        const value = Math.round(entry.percent * 100)
        return `${entry.name}(${value}%)`
    }

    const renderLegend = (props) => {
        const { payload } = props
        return (
            <ul className={style.legendItems}>
                {
                    payload.map((entry, index) => (
                        <li className={style.legendItem} key={`item-${index}`}><div className={style.legendNode} style={{ background: performanceDefaultColor }}></div>{performanceMatricsList[entry.value]}</li>
                    ))
                }
            </ul>
        )
    }

    const data = [
        {
            'name': 'Mon',
            'value': 24
        },
        {
            'name': 'Tue',
            'value': 22
        },
        {
            'name': 'Wed',
            'value': 22
        },
        {
            'name': 'Thu',
            'value': 2
        },
        {
            'name': 'Fri',
            'value': 18
        },
        {
            'name': 'Sat',
            'value': 5
        },
        {
            'name': 'Sun',
            'value': 10
        }
    ]


    const AgentWeekReport = {
        table: 'Metrics',
        data: [
            {
                key: 'Fastest Response',
                value: 'Tuesday'
            },
            {
                key: 'Longest Response',
                value: 'Saturday'
            },
        ]
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const labelInfo = payload[0].payload
            // console.log('labelInfo: ', labelInfo)
            return (
                <div className={style.customToolTip}>
                    <p className={style.maiLabel}>{label}</p>
                    {/* <p className={style.desc}>Anything you want can be displayed here.</p> */}
                    {/* <p className={style.label1}><b style={{color: labelInfo.Value1Color}}>{`${label} : ${labelInfo.value1}`}</b></p>
                    <p className={style.label1}><b style={{color: labelInfo.Value1Color}}>{`${label} : ${labelInfo.value1}`}</b></p> */}
                </div>
            )
        }

        return null
    }


    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div onClick={props.toggleAnalyticsWindow} style={{ cursor: 'pointer', width: '20px', height: '20px' }}>
                        <BackIcon />
                    </div>
                    <span style={{ fontFamily: 'Barlow Medium', fontSize: '24px', paddingLeft: '20px' }}>
                        {lang.tr('admin.workspace.users.collaborators.advance_report')}
                    </span>
                </div>
                <div style={{ width: '220px' }}>
                    <Select
                        id="agents"
                        default={AgentList[0]}
                        style={{ zIndex: 1000 }}
                        options={AgentList}
                        value={selectedOption}
                        onChange={handleOptionSelect}
                    />
                </div>
            </div>
            <div className={style.agentAnalyticsSectionContainer}>
                {!selectedAgentID ? <>
                    <div id='analytics-section1' className={`${style.sectionsWrapper} ${style.analyticsSection1}`}>
                        <div>
                            <h2>Live Performance Matrics</h2>
                            <ResponsiveContainer height={190}>
                                <BarChart
                                    width={500}
                                    height={320}
                                    data={Bar1data}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                    barSize={20}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip cursor={{ fill: 'transparent' }} content={({ active, payload, label }: any) => {
                                        if (active && payload && payload.length) {
                                            const labelInfo = payload[0].payload
                                            // console.log('labelInfo: ', labelInfo)
                                            return (
                                                <div className={style.customToolTip}>
                                                    <p className={style.maiLabel}>{label}</p>
                                                    <p className={style.desc}>Live Performance Matrics</p>
                                                    {labelInfo.name === 'Sessions' ? <p className={style.label} style={{ color: labelInfo.Value1Color }}>{`Average Sessions : ${labelInfo.Value1}`}</p> :
                                                        <>
                                                            {labelInfo.Value2 ? <p className={style.label} style={{ color: labelInfo.Value2Color }}>{`Unlinked : ${labelInfo.Value2}`}</p> : <></>}
                                                            {labelInfo.Value1 ? <p className={style.label} style={{ color: labelInfo.Value1Color }}>{`Linked : ${labelInfo.Value1}`}</p> : <></>}
                                                        </>
                                                    }
                                                </div>
                                            )
                                        }
                                    }} />
                                    <Bar dataKey="Value1" stackId="a"
                                        shape={(props) => {
                                            // console.log(props)
                                            const newProps = { ...props, fill: props.Value1Color }
                                            return <Rectangle {...newProps} />
                                        }}
                                    />
                                    <Bar dataKey="Value2" stackId="a"
                                        shape={(props) => {
                                            // console.log(props)
                                            const newProps = { ...props, fill: props.Value2Color }
                                            return <Rectangle {...newProps} />
                                        }}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div id='analytics-section2' className={`${style.sectionsWrapper} ${style.analyticsSection2}`}>
                        <PerformanceGraph type='agentPerformance' heading='Agent Performance Comparison' dataKey='email' agentsStatsData={props.agentsStatsData} performanceMatricsList={performanceMatricsList} defaultColor={performanceDefaultColor} backgroundColor={performanceBackgroundColor} />
                        <PerformanceGraph type='botPerformance' heading='Bot Activity Comparison' dataKey='botName' agentsStatsData={props.agentsStatsData} performanceMatricsList={performanceMatricsList} botNames={botNames} defaultColor={performanceDefaultColor} backgroundColor={performanceBackgroundColor} />
                    </div>
                    <div id='analytics-section3' className={`${style.sectionsWrapper} ${style.analyticsSection3}`}>
                        <PerformancePieChart type='agentPerformance' heading='Agent Performance (Resolved Requests)' agentsStatsData={props.agentsStatsData} performancePieChartList={performancePieChartList} filterEnable={true} botNames={botNames}/>
                        <PerformancePieChart type='botPerformance' heading='Bot Performance (Resolved Requests)' agentsStatsData={props.agentsStatsData} performancePieChartList={performancePieChartList} botNames={botNames}/>
                    </div>

                </> : <>
                    <div id='analytics-section1' className={`${style.sectionsWrapper} ${style.analyticsSection1}`}>
                        <div>
                            <h2>Agent Performance Report</h2>
                            <TableContainer
                                data={selectedAgentStatsData.length > 0 ? selectedAgentStatsData : agentBotAssignments}
                                botNames={botNames}
                                tableValueHeading='Value'
                            />
                        </div>
                        <PerformanceGraph type='botPerformance' heading='Bot Activity Comparison' dataKey='botName' agentsStatsData={selectedAgentStatsData} performanceMatricsList={performanceMatricsList} botNames={botNames} defaultColor={performanceDefaultColor} backgroundColor={performanceBackgroundColor} />
                    </div>
                </>}
            </div>
        </div >
    )
}

const mapStateToProps = (state: AppState) => ({
    agentsStatsData: state.collaborators.agentsStatsData,
    bots: state.bots.bots,
    botsByAgent: state.bots.botsByAgent,
    users: state.collaborators.users,
    profile: state.user.profile
})

const connector = connect(mapStateToProps, { fetchAgentsStatsData, toggleAnalyticsWindow })

export default connector(ReportingModal)
